import { ChangeEvent, MouseEvent } from 'react';
import { Row, Col, Form, DropdownButton, Dropdown } from 'react-bootstrap';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { WtoRule } from '../../wto-form-context';
import { VariantSplits } from './VariantSplits';
import { TimeSelection } from './TimeSelection';
import { TIMEZONE_MAP_KEYS } from './rules-form-modal-constants';

interface TrafficAllocationSectionProps {
  newRule: WtoRule;
  wtoFormObj: Plover;
  readOnly: boolean;
  minYear: number;
  maxYear: number;
  onSelectChange: (
    event: unknown,
    keyName?: 'additional_utm' | 'additional_utm_operation' | 'variant_id' | 'cookie_operation',
    index?: number,
    index2?: number
  ) => void;
  onInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    valueType?: string,
    idx?: number
  ) => void;
  onDeleteOperationClick: (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => void;
  handleDowVariant: (
    operation: 'select' | 'allocate' | 'delete',
    index: number,
    rowIndex?: number,
    data?: unknown
  ) => void;
  addTrafficSplit: () => void;
  addDowTrafficSplit: (index: number) => void;
  toggleCustomRetention: (event: boolean) => void;
  handleCustomRetentionDate: (date: string) => void;
  handleCustomRetentionTime: (value: string | null, type: 'hour' | 'minute') => void;
  handleCustomRetentionPeriod: (period: 'am' | 'pm' | null) => void;
  handleCustomRetentionTimezone: (timezone: string | null) => void;
}

export const TrafficAllocationSection = ({
  newRule,
  wtoFormObj,
  readOnly,
  minYear,
  maxYear,
  onSelectChange,
  onInputChange,
  onDeleteOperationClick,
  handleDowVariant,
  addTrafficSplit,
  addDowTrafficSplit,
  toggleCustomRetention,
  handleCustomRetentionDate,
  handleCustomRetentionTime,
  handleCustomRetentionPeriod,
  handleCustomRetentionTimezone
}: TrafficAllocationSectionProps) => (
  <Row>
    <Form.Group as={Col} className='rule-editor'>
      <span className='rule-header'>Traffic Allocation</span>
      <VariantSplits
        variants={newRule.variant_splits}
        readOnly={readOnly}
        newRule={newRule}
        wtoFormObj={wtoFormObj}
        onSelectChange={onSelectChange}
        onInputChange={onInputChange}
        onDeleteOperationClick={onDeleteOperationClick}
        handleDowVariant={handleDowVariant}
        addTrafficSplit={addTrafficSplit}
        addDowTrafficSplit={addDowTrafficSplit}
      />
      <Form.Check
        disabled={readOnly}
        className='retention-checkbox'
        label='Reset Variant Retention'
        type='checkbox'
        checked={newRule.customRetention?.customRetentionStatus || false}
        onChange={e => toggleCustomRetention(e.target.checked)}
      />
      {newRule.customRetention?.customRetentionStatus && (
        <div className='retention-wrapper'>
          Select date of reset
          <DatePicker
            isSingle
            disabled={readOnly}
            minYear={minYear}
            maxYear={maxYear}
            range={
              newRule.customRetention.startDate
                ? {
                    start: newRule.customRetention.startDate,
                    end: newRule.customRetention.startDate
                  }
                : undefined
            }
            onChange={newDates => handleCustomRetentionDate(newDates.start)}
          />
          <TimeSelection
            readOnly={readOnly}
            handleHour={hour => handleCustomRetentionTime(hour, 'hour')}
            handleMinute={minute => handleCustomRetentionTime(minute, 'minute')}
            handleAmpm={period => handleCustomRetentionPeriod(period)}
            timestamp={newRule.customRetention.startDate}
          />
          <DropdownButton
            disabled={readOnly}
            className='condition-dropdown'
            title={
              newRule.customRetention.time_zone
                ? newRule.customRetention.time_zone
                : 'Select Timezone'
            }
          >
            {TIMEZONE_MAP_KEYS.map(timezone => (
              <Dropdown.Item
                key={timezone}
                eventKey={timezone}
                onClick={() => handleCustomRetentionTimezone(timezone)}
              >
                {timezone}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
      )}
      <p>Note: Checking this will randomize variants for returning users</p>
    </Form.Group>
  </Row>
);
